import React, { CSSProperties, ComponentProps } from 'react'
import { Tooltip, Chip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { keyframes } from 'tss-react'
import { makeStyles } from 'tss-react/mui'
import { Nullable } from 'types/global'

const useStyles = makeStyles()(theme => ({
  skill: {
    margin: theme.spacing(0, 1, 1, 0),
    display: 'inline-block',
    animation: `${keyframes`
      from {
        opacity: 0;
        transform: scale(0);
      }
      to {
        opacity: 1;
        transform: scale(1);
      }
    `} 0.5s cubic-bezier(0.8, 0, 0, 1)`,
    animationDelay: '800ms',
    animationFillMode: 'backwards',
  },
  skillContent: {
    '&:nth-of-type(1)': {
      animationDelay: '200ms',
    },
    '&:nth-of-type(2)': {
      animationDelay: '300ms',
    },
    '&:nth-of-type(3)': {
      animationDelay: '400ms',
    },
    '&:nth-of-type(4)': {
      animationDelay: '500ms',
    },
    '&:nth-of-type(5)': {
      animationDelay: '600ms',
    },
    '&:nth-of-type(6)': {
      animationDelay: '700ms',
    },
  },
}))

interface ISkillChipProps {
  skill_experience: {
    experience?: Nullable<number>
    name: string
    id?: number
    required_years?: Nullable<number>
  }
  icon?: ComponentProps<typeof Chip>['icon']
  required?: boolean
  animated?: boolean
  focused?: boolean
  flat?: boolean
  hideExperience?: boolean
}

function SkillChip(props: ISkillChipProps) {
  const { classes } = useStyles()
  const theme = useTheme()

  const { skill_experience, animated = true, icon, required, hideExperience, focused, flat } = props

  const title = [skill_experience.name]
  let style: CSSProperties = {}

  const renderExperience = () => {
    if (required) {
      if ((skill_experience.required_years || 0) > 0) {
        return `(${skill_experience.required_years}+ years)`
      }

      return ''
    }

    if (!hideExperience && skill_experience.experience) {
      return `${skill_experience.experience}+ yrs`
    }

    return ''
  }

  const renderWholeExperience = () => {
    if (hideExperience) {
      return ''
    }

    if (!skill_experience.experience) {
      return '(skill)'
    }

    if (skill_experience.experience === 1) {
      return '(skill, 1+ year experience)'
    }

    return `(skill, ${skill_experience.experience}+ years experience)`
  }

  if (required) {
    title.push('(required)')
  }

  if (!hideExperience) {
    title.push(renderWholeExperience())
  }

  if (!animated) {
    style.animation = 'none'
  }

  const focusedStyle = {
    background: theme.palette.primary.main,
    color: '#FFF',
  }
  const unfocusedStyle = flat ? { background: 'none' } : undefined
  const chipStyle = focused ? focusedStyle : unfocusedStyle

  return (
    <div
      className={classes.skill}
      key={skill_experience.id || skill_experience.name}
      data-cy={`freelancer-chip-skill-${skill_experience.name}`}
      style={style}
    >
      <Tooltip title={flat ? '' : title.join(' ')}>
        <Chip
          icon={icon}
          style={chipStyle}
          label={(
            <Typography variant="body2" className={classes.skillContent}>
              {skill_experience.name} {renderExperience()}
            </Typography>
          )}
        />
      </Tooltip>
    </div>
  )
}

export default SkillChip
